// Generated by ReScript, PLEASE EDIT WITH CARE
'use strict';

var React = require("react");
var Js_math = require("rescript/lib/js/js_math.js");
var Belt_Array = require("rescript/lib/js/belt_Array.js");
var Caml_array = require("rescript/lib/js/caml_array.js");
var Client = require("react-dom/client");
var JsxRuntime = require("react/jsx-runtime");

function cryptoRandoms(min, max, amount) {
  var values = (crypto.getRandomValues(new Uint16Array(amount)));
  var values$1 = Array.from(values);
  var pow = Math.pow(2.0, 16.0);
  var values$2 = values$1.map(function (n) {
        return n / pow * (max - min) + min;
      });
  return values$2.map(function (prim) {
              return Math.floor(prim);
            });
}

var useInterval = (function useInterval(callback, delay) {
  const savedCallback = React.useRef();

  // Remember the latest callback.
  React.useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  React.useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
});

function isDivisibleBy3(value) {
  switch (value.TAG) {
    case "BareNumber" :
        return value._0 % 3 === 0;
    case "PlusNumber" :
        return (value._0 + value._1 | 0) % 3 === 0;
    case "MinusNumber" :
        return (value._0 - value._1 | 0) % 3 === 0;
    
  }
}

function cellToString(cell) {
  var n = cell.value;
  switch (n.TAG) {
    case "BareNumber" :
        return n._0.toString();
    case "PlusNumber" :
        var x = n._0.toString();
        var y = n._1.toString();
        return x + " + " + y;
    case "MinusNumber" :
        var x$1 = n._0.toString();
        var y$1 = n._1.toString();
        return x$1 + " - " + y$1;
    
  }
}

function cryptoRandomOne(min, max) {
  var min$1 = min;
  var max$1 = max;
  var n = Caml_array.get(cryptoRandoms(min$1, max$1, 1), 0);
  return {
          id: crypto.randomUUID(),
          value: {
            TAG: "BareNumber",
            _0: n
          },
          status: "Normal"
        };
}

function randomOne(min, max) {
  var n = Js_math.random_int(min, max);
  return {
          id: crypto.randomUUID(),
          value: {
            TAG: "BareNumber",
            _0: n
          },
          status: "Normal"
        };
}

function randomOneExp(min, max) {
  var x = Js_math.random_int(min, max);
  var ymax = Math.min(x, 99);
  var y = Js_math.random_int(1, ymax);
  var generatePlus = Js_math.random_int(1, 12) > 3;
  var value = generatePlus ? ({
        TAG: "PlusNumber",
        _0: x,
        _1: y
      }) : ({
        TAG: "MinusNumber",
        _0: x,
        _1: y
      });
  return {
          id: crypto.randomUUID(),
          value: value,
          status: "Normal"
        };
}

function isSameValue(valA, valB) {
  switch (valA.TAG) {
    case "BareNumber" :
        switch (valB.TAG) {
          case "BareNumber" :
              return valA._0 === valB._0;
          case "PlusNumber" :
          case "MinusNumber" :
              return false;
          
        }
    case "PlusNumber" :
        switch (valB.TAG) {
          case "PlusNumber" :
              if (valA._0 === valB._0) {
                return valA._1 === valB._1;
              } else {
                return false;
              }
          case "BareNumber" :
          case "MinusNumber" :
              return false;
          
        }
    case "MinusNumber" :
        switch (valB.TAG) {
          case "BareNumber" :
          case "PlusNumber" :
              return false;
          case "MinusNumber" :
              if (valA._0 === valB._0) {
                return valA._1 === valB._1;
              } else {
                return false;
              }
          
        }
    
  }
}

function randomMany(min, max, amount, exprate) {
  while(true) {
    var cells = [];
    var hasAnswer = false;
    while(cells.length < amount) {
      var generateExpCell = Math.random() < exprate;
      var newcell = generateExpCell ? randomOneExp(min, max) : randomOne(min, max);
      var alreadyExists = cells.some((function(newcell){
          return function (cell) {
            return isSameValue(cell.value, newcell.value);
          }
          }(newcell)));
      if (!alreadyExists) {
        if (isDivisibleBy3(newcell.value)) {
          hasAnswer = true;
        }
        cells.push(newcell);
      }
      
    };
    if (hasAnswer) {
      return cells;
    }
    continue ;
  };
}

function validate(cells) {
  return cells.every(function (cell) {
              var should_select = isDivisibleBy3(cell.value);
              var match = cell.status;
              if (match === "Normal") {
                return !should_select;
              } else {
                return should_select;
              }
            });
}

var Logic = {
  isDivisibleBy3: isDivisibleBy3,
  cellToString: cellToString,
  cryptoRandomOne: cryptoRandomOne,
  randomOne: randomOne,
  randomOneExp: randomOneExp,
  isSameValue: isSameValue,
  randomMany: randomMany,
  validate: validate
};

var initialState = {
  startedAt: 0,
  difficulty: "Mini",
  remainedBoards: 0,
  board: "Idle",
  incorrectSubmits: 0
};

function generateCellsByDifficulty(difficulty) {
  switch (difficulty) {
    case "Mini" :
        return randomMany(10, 99, 6, 0);
    case "Easy" :
        return randomMany(10, 99, 9, 0);
    case "Moderate" :
        return randomMany(100, 999, 9, 0);
    case "Hard" :
        return randomMany(1000, 9999, 9, 0);
    case "Horrible" :
        return randomMany(1000, 9999, 9, 0.333);
    
  }
}

function difficultyToString(difficulty) {
  switch (difficulty) {
    case "Mini" :
        return "Mini";
    case "Easy" :
        return "Easy";
    case "Moderate" :
        return "Moderate";
    case "Hard" :
        return "Hard";
    case "Horrible" :
        return "Horrible";
    
  }
}

function reducer(state, action) {
  if (typeof action !== "object") {
    switch (action) {
      case "ResetBoard" :
          return {
                  startedAt: 0,
                  difficulty: state.difficulty,
                  remainedBoards: 10,
                  board: "Idle",
                  incorrectSubmits: 0
                };
      case "NextBoard" :
          if ((state.remainedBoards - 1 | 0) > 0) {
            var cells = generateCellsByDifficulty(state.difficulty);
            return {
                    startedAt: state.startedAt,
                    difficulty: state.difficulty,
                    remainedBoards: state.remainedBoards - 1 | 0,
                    board: {
                      TAG: "Playing",
                      _0: cells
                    },
                    incorrectSubmits: state.incorrectSubmits
                  };
          }
          var now = Date.now();
          var duration = now - state.startedAt;
          return {
                  startedAt: state.startedAt,
                  difficulty: state.difficulty,
                  remainedBoards: state.remainedBoards,
                  board: {
                    TAG: "Ended",
                    _0: duration,
                    _1: state.incorrectSubmits
                  },
                  incorrectSubmits: state.incorrectSubmits
                };
      case "HandleIncorrectSubmit" :
          var cells$1 = state.board;
          if (typeof cells$1 !== "object") {
            return state;
          }
          if (cells$1.TAG !== "Playing") {
            return state;
          }
          var modifiedCells = cells$1._0.map(function (cell) {
                return {
                        id: cell.id,
                        value: cell.value,
                        status: "Normal"
                      };
              });
          return {
                  startedAt: state.startedAt,
                  difficulty: state.difficulty,
                  remainedBoards: state.remainedBoards,
                  board: {
                    TAG: "Playing",
                    _0: modifiedCells
                  },
                  incorrectSubmits: state.incorrectSubmits + 1 | 0
                };
      
    }
  } else {
    if (action.TAG === "NewBoard") {
      var difficulty = action._0;
      return {
              startedAt: Date.now(),
              difficulty: difficulty,
              remainedBoards: 10,
              board: {
                TAG: "Playing",
                _0: generateCellsByDifficulty(difficulty)
              },
              incorrectSubmits: 0
            };
    }
    var cellIdToToggle = action._0;
    var cells$2 = state.board;
    if (typeof cells$2 !== "object") {
      return state;
    }
    if (cells$2.TAG !== "Playing") {
      return state;
    }
    var modifiedCells$1 = cells$2._0.map(function (cell) {
          if (cell.id !== cellIdToToggle) {
            return cell;
          }
          var match = cell.status;
          if (match === "Normal") {
            return {
                    id: cell.id,
                    value: cell.value,
                    status: "Selected"
                  };
          } else {
            return {
                    id: cell.id,
                    value: cell.value,
                    status: "Normal"
                  };
          }
        });
    return {
            startedAt: state.startedAt,
            difficulty: state.difficulty,
            remainedBoards: state.remainedBoards,
            board: {
              TAG: "Playing",
              _0: modifiedCells$1
            },
            incorrectSubmits: state.incorrectSubmits
          };
  }
}

var context = React.createContext([
      initialState,
      (function (param) {
          
        })
    ]);

var provider = context.Provider;

function App$GameStateContext$Provider(props) {
  return React.createElement(provider, {
              value: props.value,
              children: props.children
            });
}

var Provider = {
  provider: provider,
  make: App$GameStateContext$Provider
};

var GameStateContext = {
  context: context,
  Provider: Provider
};

function App$NewGameForm$DifficultyOption(props) {
  var text = difficultyToString(props.value);
  return JsxRuntime.jsx("option", {
              children: text,
              value: text
            });
}

var DifficultyOption = {
  make: App$NewGameForm$DifficultyOption
};

function App$NewGameForm(props) {
  var match = React.useContext(context);
  var dispatch = match[1];
  var onSubmit = function ($$event) {
    $$event.preventDefault();
    var target = $$event.target;
    var difficultyValue = target.elements.difficulty.value;
    switch (difficultyValue) {
      case "Easy" :
          return dispatch({
                      TAG: "NewBoard",
                      _0: "Easy"
                    });
      case "Hard" :
          return dispatch({
                      TAG: "NewBoard",
                      _0: "Hard"
                    });
      case "Horrible" :
          return dispatch({
                      TAG: "NewBoard",
                      _0: "Horrible"
                    });
      case "Mini" :
          return dispatch({
                      TAG: "NewBoard",
                      _0: "Mini"
                    });
      case "Moderate" :
          return dispatch({
                      TAG: "NewBoard",
                      _0: "Moderate"
                    });
      default:
        return dispatch({
                    TAG: "NewBoard",
                    _0: "Mini"
                  });
    }
  };
  return JsxRuntime.jsxs("form", {
              children: [
                JsxRuntime.jsxs("label", {
                      children: [
                        "Difficulty: ",
                        JsxRuntime.jsxs("select", {
                              children: [
                                JsxRuntime.jsx(App$NewGameForm$DifficultyOption, {
                                      value: "Mini"
                                    }),
                                JsxRuntime.jsx(App$NewGameForm$DifficultyOption, {
                                      value: "Easy"
                                    }),
                                JsxRuntime.jsx(App$NewGameForm$DifficultyOption, {
                                      value: "Moderate"
                                    }),
                                JsxRuntime.jsx(App$NewGameForm$DifficultyOption, {
                                      value: "Hard"
                                    }),
                                JsxRuntime.jsx(App$NewGameForm$DifficultyOption, {
                                      value: "Horrible"
                                    })
                              ],
                              defaultValue: "mini",
                              className: "bg-slate-300 text-xl p-2 rounded-lg",
                              name: "difficulty"
                            })
                      ]
                    }),
                JsxRuntime.jsx("input", {
                      className: "m-2 py-2 px-8 rounded-lg text-center text-xl bg-yellow-300",
                      type: "submit",
                      value: "Start!"
                    })
              ],
              onSubmit: onSubmit
            });
}

var NewGameForm = {
  DifficultyOption: DifficultyOption,
  make: App$NewGameForm
};

function App$Cell(props) {
  var cell = props.cell;
  var match = React.useContext(context);
  var dispatch = match[1];
  var match$1 = cell.value;
  var textSizeClassName;
  switch (match$1.TAG) {
    case "BareNumber" :
        textSizeClassName = "text-2xl";
        break;
    case "PlusNumber" :
    case "MinusNumber" :
        textSizeClassName = "text-xl";
        break;
    
  }
  var match$2 = cell.status;
  var classNameByStatus;
  classNameByStatus = match$2 === "Normal" ? "bg-slate-300 rounded-lg" : "bg-yellow-400 font-bold rounded-lg";
  var className = "block py-5 px-1 w-full transition-colors " + textSizeClassName + " " + classNameByStatus;
  var text = cellToString(cell);
  return JsxRuntime.jsx("div", {
              children: JsxRuntime.jsx("button", {
                    children: text,
                    className: className,
                    type: "button",
                    onClick: (function ($$event) {
                        dispatch({
                              TAG: "ToggleSelection",
                              _0: cell.id
                            });
                      })
                  }),
              className: "py-1 px-1 text-center"
            });
}

var Cell = {
  make: App$Cell
};

function App$Board$IdleBoard(props) {
  return JsxRuntime.jsxs("div", {
              children: [
                JsxRuntime.jsx("big", {
                      children: "< Trivide >"
                    }),
                JsxRuntime.jsx("p", {
                      children: "Select every buttons that can be divisible by 3, then click 'Next'"
                    }),
                JsxRuntime.jsx(App$NewGameForm, {})
              ],
              className: "text-center"
            });
}

var IdleBoard = {
  make: App$Board$IdleBoard
};

function App$Board$ResultBoard(props) {
  var match = React.useContext(context);
  var dispatch = match[1];
  var onNewGameButtonClicked = function (_event) {
    dispatch("ResetBoard");
  };
  var difficulty = difficultyToString(match[0].difficulty);
  var durationInSeconds = props.duration / 1000;
  var durationInSeconds$1 = durationInSeconds.toFixed(2);
  return JsxRuntime.jsxs("div", {
              children: [
                JsxRuntime.jsx("div", {
                      children: "Cleared!",
                      className: "my-3 text-2xl"
                    }),
                JsxRuntime.jsx("div", {
                      children: "Difficulty: " + difficulty,
                      className: "my-3 text-1xl"
                    }),
                JsxRuntime.jsx("div", {
                      children: "Duration: " + durationInSeconds$1 + "s",
                      className: "my-3 text-1xl"
                    }),
                JsxRuntime.jsx("div", {
                      children: "Incorrects: " + props.incorrects.toString() + " times",
                      className: "my-3 text-1xl"
                    }),
                JsxRuntime.jsx("hr", {}),
                JsxRuntime.jsx("div", {
                      children: JsxRuntime.jsx("input", {
                            className: "w-full py-2 text-2xl bg-yellow-300",
                            type: "button",
                            value: "New game",
                            onClick: onNewGameButtonClicked
                          }),
                      className: "my-3"
                    })
              ],
              className: "text-center px-2 result"
            });
}

var ResultBoard = {
  make: App$Board$ResultBoard
};

function App$Board$PlayingBoard(props) {
  var cells = Belt_Array.map(props.cells, (function (cell) {
          return JsxRuntime.jsx(App$Cell, {
                      cell: cell
                    }, cell.id);
        }));
  return JsxRuntime.jsx("div", {
              children: cells,
              className: "grid grid-cols-3"
            });
}

var PlayingBoard = {
  make: App$Board$PlayingBoard
};

function isPlaying(board) {
  if (typeof board !== "object" || board.TAG !== "Playing") {
    return false;
  } else {
    return true;
  }
}

function App$Board(props) {
  var board = props.board;
  var inner;
  inner = typeof board !== "object" ? JsxRuntime.jsx(App$Board$IdleBoard, {}) : (
      board.TAG === "Playing" ? JsxRuntime.jsx(App$Board$PlayingBoard, {
              cells: board._0
            }) : JsxRuntime.jsx(App$Board$ResultBoard, {
              duration: board._0,
              incorrects: board._1
            })
    );
  return JsxRuntime.jsx("div", {
              children: inner,
              className: "p-2 bg-slate-100"
            });
}

var Board = {
  IdleBoard: IdleBoard,
  ResultBoard: ResultBoard,
  PlayingBoard: PlayingBoard,
  isPlaying: isPlaying,
  make: App$Board
};

function App$Controller(props) {
  var match = React.useContext(context);
  var dispatch = match[1];
  var state = match[0];
  var match$1 = React.useState(function () {
        return "";
      });
  var setTimeText = match$1[1];
  var isPlaying$1 = isPlaying(state.board);
  var onNewGameButtonClick = function (_event) {
    var confirmed = window.confirm("Are you sure to start new game?");
    if (confirmed) {
      setTimeText(function (param) {
            return "";
          });
      return dispatch("ResetBoard");
    }
    
  };
  var onSubmitButtonClick = function (_event) {
    var cells = state.board;
    if (typeof cells !== "object") {
      return ;
    }
    if (cells.TAG !== "Playing") {
      return ;
    }
    var isValid = validate(cells._0);
    if (isValid) {
      return dispatch("NextBoard");
    } else {
      return dispatch("HandleIncorrectSubmit");
    }
  };
  useInterval((function () {
          var match = state.board;
          if (typeof match !== "object") {
            return ;
          }
          if (match.TAG !== "Playing") {
            return ;
          }
          var time = Date.now() - state.startedAt;
          var time$1 = time / 1000;
          var time$2 = time$1.toFixed();
          setTimeText(function (param) {
                return time$2;
              });
        }), 100);
  var clearedBoards = 10 - state.remainedBoards | 0;
  return JsxRuntime.jsxs("div", {
              children: [
                JsxRuntime.jsx("input", {
                      className: "w-full py-2 text-2xl bg-yellow-300 rounded-lg",
                      disabled: !isPlaying$1,
                      type: "button",
                      value: "Next",
                      onClick: onSubmitButtonClick
                    }),
                JsxRuntime.jsxs("div", {
                      children: [
                        JsxRuntime.jsx("div", {
                              children: JsxRuntime.jsx("span", {
                                    children: "Cleared: " + clearedBoards.toString() + " / " + (10).toString(),
                                    className: "text-xl"
                                  })
                            }),
                        JsxRuntime.jsx("div", {
                              children: JsxRuntime.jsx("span", {
                                    children: "Time: " + match$1[0] + " "
                                  })
                            }),
                        JsxRuntime.jsx("div", {
                              children: JsxRuntime.jsx("span", {
                                    children: "Incorrects: " + state.incorrectSubmits.toString()
                                  })
                            }),
                        isPlaying$1 ? JsxRuntime.jsx("div", {
                                children: JsxRuntime.jsx("input", {
                                      className: "bg-slate-300 py-2 px-6 rounded-lg",
                                      disabled: !isPlaying$1,
                                      type: "button",
                                      value: "New game",
                                      onClick: onNewGameButtonClick
                                    }),
                                className: "my-4"
                              }) : JsxRuntime.jsx(JsxRuntime.Fragment, {})
                      ],
                      className: "mx-auto py-4"
                    })
              ],
              className: "px-2 my-4 mx-auto flex flex-col"
            });
}

var Controller = {
  make: App$Controller
};

function App$Game(props) {
  var match = React.useReducer(reducer, initialState);
  var state = match[0];
  var isPlaying$1 = isPlaying(state.board);
  return JsxRuntime.jsxs(App$GameStateContext$Provider, {
              value: [
                state,
                match[1]
              ],
              children: [
                JsxRuntime.jsx(App$Board, {
                      board: state.board
                    }),
                isPlaying$1 ? JsxRuntime.jsx(App$Controller, {}) : null
              ]
            });
}

var Game = {
  make: App$Game
};

var root = document.querySelector("#app");

if (!(root == null)) {
  var root$1 = Client.createRoot(root);
  root$1.render(JsxRuntime.jsx(App$Game, {}));
}

var maxBoards = 10;

exports.maxBoards = maxBoards;
exports.cryptoRandoms = cryptoRandoms;
exports.useInterval = useInterval;
exports.Logic = Logic;
exports.initialState = initialState;
exports.generateCellsByDifficulty = generateCellsByDifficulty;
exports.difficultyToString = difficultyToString;
exports.reducer = reducer;
exports.GameStateContext = GameStateContext;
exports.NewGameForm = NewGameForm;
exports.Cell = Cell;
exports.Board = Board;
exports.Controller = Controller;
exports.Game = Game;
/* context Not a pure module */
